/**
 * Risolve nome/cognome da pazienteId per il log promemoria (D-09 / P-B1).
 * ADMIN non chiama Firestore: i nominativi richiedono un ruolo clinico.
 */

import { useQuery } from '@tanstack/react-query';
import { getPazientiBatch } from '../services/agenda';
import { useAuth } from './useAuth';

export type PazienteDisplay =
  | { kind: 'name'; nome: string; cognome: string; email?: string }
  | { kind: 'unavailable' }
  | { kind: 'adminId' };

export function usePazienteDisplayNames(ids: readonly string[]) {
  const { role, isBackoffice } = useAuth();
  const isAdmin = role === 'ADMIN';
  const dedup = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  const key = dedup.join(',');

  const query = useQuery({
    // PR-BO: chiavi distinte per account backoffice e non, che risolvono
    // insiemi di pazienti diversi.
    queryKey: ['promemoria-paziente-names', key, isBackoffice] as const,
    queryFn: async () => {
      const map = await getPazientiBatch(dedup, isBackoffice);
      const out = new Map<string, PazienteDisplay>();
      for (const id of dedup) {
        const p = map.get(id);
        if (!p) {
          out.set(id, { kind: 'unavailable' });
          continue;
        }
        out.set(id, {
          kind: 'name',
          nome: p.nome ?? '',
          cognome: p.cognome ?? '',
          email: typeof p.email === 'string' ? p.email : undefined,
        });
      }
      return out;
    },
    enabled: !isAdmin && dedup.length > 0,
    staleTime: 5 * 60_000,
    retry: 0,
  });

  function resolve(id: string): PazienteDisplay {
    if (isAdmin) return { kind: 'adminId' };
    return query.data?.get(id) ?? { kind: 'unavailable' };
  }

  return {
    resolve,
    isLoading: !isAdmin && query.isLoading,
  };
}
